import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  IconButton,
  useTheme,
  Button,
  Grid,
  Dialog,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DashboardHeader from "../Dashboard/DashboardHeader";
import { useNavigate } from "react-router-dom";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { baseapi } from "../Environmentalurl";
import {
  ApplicationType,
  entity_id,
  generateOrderId,
  location,
  Platform,
} from "../Utils/Const";
import { useDispatch } from "react-redux";
import { setLoginMeta } from "../Slice/authSlice";
import LoadMoneyBankSection from "./LoadMoneyBankSection";
import { Link } from "react-router-dom";
import ConstructionIcon from "@mui/icons-material/Construction";
import CloseIcon from "@mui/icons-material/Close";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function WalletLoadMoney() {
  const [user, setUser] = useState({});
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openComingSoon, setOpenComingSoon] = useState(false);
  const [comingSoonTitle, setComingSoonTitle] = useState("");
  const [amountError, setAmountError] = useState("");

  // ── Cashfree inline state ──────────────────────────────────────────────────
  const [cashfreeLoading, setCashfreeLoading] = useState(false);
  const [cashfreeResult, setCashfreeResult] = useState(null);

  const ip = useIPAddress();
  const [amount, setAmount] = useState("100.00");

  // ── KYC-based amount limits ────────────────────────────────────────────────
  const isKycDone =
    String(sessionStorage.getItem("Is_KYC_Done") || "").replace(/"/g, "") === "1";
  const MAX_AMOUNT = isKycDone ? 200000 : 10000;
  const MIN_AMOUNT = 10;
  const maxLabel = MAX_AMOUNT.toLocaleString("en-IN");

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
  };

  const SessionID = sessionStorage.getItem("SessionID");
  const mobilenumber = sessionStorage.getItem("mobileNumber");

  const handleTapayPayment = async (method) => {
    const num = parseFloat(amount);
    if (!amount || isNaN(num) || num < MIN_AMOUNT) {
      setAmountError(`Minimum amount should be ₹${MIN_AMOUNT}`);
      return;
    }
    if (num > MAX_AMOUNT) {
      setAmountError(`Maximum allowed is ₹${maxLabel}`);
      return;
    }

    try {
      const mobileNumber = sessionStorage.getItem("mobileNumber") || "";
      const accountNo = sessionStorage.getItem("accountNo") || "";

      const newOrderId = generateOrderId({ mobileNumber, accountNo });

      const payload = {
        ReqType: "TA_PG_Credit",
        Statement: "CheckFundingLimit",
        Mobilenumber: mobilenumber,
        SessionID: SessionID,
        TrxnAmount: parseFloat(amount).toFixed(2),
        OrderId: newOrderId,
        ServiceID: "5",
        Narration: `Load Money to Wallet Amount of ${amount}`,
        MachineID: ip,
        Platform: Platform,
        EntityID: entity_id,
        GeoLocation: location,
      };

      const response = await api.post("/tapgcredit", payload);
      const data = response.data;

      if (data.Response_Code === "1000") {
        dispatch(
          setLoginMeta({
            isLoggedIn: true,
            amount: payload.TrxnAmount,
            orderId: newOrderId,
          }),
        );

        navigate(method.path, { state: { amount } });
      } else {
        alert(data.Message || "Transaction failed");
      }
    } catch (error) {
      console.error(error);
      alert("API Error. Please try again.");
    }
  };

  // ── Cashfree Payment Gateway — inline ─────────────────────────────────────
  const cashfreeRunningRef = React.useRef(false);

  const handleCashfreePayment = async () => {
    if (cashfreeRunningRef.current) return;
    cashfreeRunningRef.current = true;

    const parsedAmount = parseFloat(amount);
    if (!amount || isNaN(parsedAmount) || parsedAmount < MIN_AMOUNT) {
      setAmountError(`Minimum amount should be ₹${MIN_AMOUNT}`);
      cashfreeRunningRef.current = false;
      return;
    }
    if (parsedAmount > MAX_AMOUNT) {
      setAmountError(`Maximum allowed is ₹${maxLabel}`);
      cashfreeRunningRef.current = false;
      return;
    }
    setAmountError("");
    setCashfreeLoading(true);

    try {
      const mobileNumber = sessionStorage.getItem("mobileNumber") || "";
      const accountNo = sessionStorage
        .getItem("virtualAccountNo")
        ?.replace(/^"|"$/g, "");

      const freshOrderId = generateOrderId({ mobileNumber, accountNo });

      const orderRes = await api.post(`${baseapi}/CashFreeOrderid_Generation`, {
        ReqType: "Cashfree_PG_Create_Order",
        Mobilenumber: mobileNumber,
        EntityID: entity_id,
        Amount: parsedAmount.toFixed(2),
        GeoLocation: location,
        Accountnumber: accountNo,
        SessionID: SessionID,
        ServiceID: "81",
        MachineID: ip,
        Platform: Platform,
        notifyUrl:
          "https://staging.transactionanalysts.com:444/TAMultiTenantAPI/TAOneapp/payment/return",
      });

      const orderData = orderRes.data;
      if (orderData.Response_Code !== "1000" || !orderData.Info?.PaymentSessionId) {
        throw new Error(orderData.Message || "Failed to create payment order.");
      }

      const { PaymentSessionId, MerchantOrderId } = orderData.Info;
      sessionStorage.setItem("cashfreeMerchantOrderId", MerchantOrderId);

      const { load } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await load({ mode: "sandbox" });

      const checkoutResult = await cashfree.checkout({
        paymentSessionId: PaymentSessionId,
        redirectTarget: "_modal",
      });

      if (checkoutResult?.error) {
        console.log("Cashfree checkout cancelled/closed by user:", checkoutResult.error);
        sessionStorage.removeItem("cashfreeMerchantOrderId");
        return;
      }

      const statusRes = await api.post(`${baseapi}/CashFreeTransactionStatus`, {
        ReqType: "Cashfree_GetTransactionStatus",
        Mobilenumber: mobileNumber,
        EntityID: entity_id,
        MerchantOrderId: MerchantOrderId,
        GeoLocation: location,
        SessionID: SessionID,
        MachineID: ip,
        Platform: Platform,
      });

      const statusData = statusRes.data;
      const payStatus = statusData?.Info?.PaymentStatus?.toUpperCase();
      const isSuccess = statusData?.Response_Code === "1000" && payStatus === "SUCCESS";
      const isPending = statusData?.Response_Code === "1200" || payStatus === "PENDING";

      if (isSuccess) sessionStorage.removeItem("cashfreeMerchantOrderId");

      setCashfreeResult({
        open: true,
        success: isSuccess,
        pending: isPending,
        message: statusData?.Message || "",
        balance: statusData?.Info?.WalletBalance || "",
        orderId: MerchantOrderId,
        txnId: statusData?.Info?.TransactionId || "",
      });
    } catch (err) {
      console.error("Cashfree error:", err);
      setCashfreeResult({
        open: true,
        success: false,
        pending: false,
        message: err.message || "Payment failed. Please try again.",
        balance: "",
        orderId: "",
        txnId: "",
      });
    } finally {
      setCashfreeLoading(false);
      cashfreeRunningRef.current = false;
    }
  };

  useEffect(() => {
    const fetchBalanceEnquiry = async () => {
      try {
        const payload = {
          ApplicationType: ApplicationType,
          EntityID: entity_id,
          GeoLocation: location,
          MachineID: ip,
          Mobilenumber: sessionStorage.getItem("mobileNumber"),
          Platform: Platform,
          ReqType: "Jan_Aadhaar_eWallet_Balance_Enquiry",
          SessionID: sessionStorage.getItem("SessionID") || "",
        };

        const response = await api.post(
          `${baseapi}/JanAadhaareWalletBalanceEnquiry`,
          payload,
        );

        if (response.data.Response_Code === "1000") {
          setUser((prev) => ({
            ...prev,
            Balance: response.data.Balance,
            LedgerBalance: response.data.LedgerBalance,
            NameOnCard: prev.NameOnCard || "User Name",
            UPIVPA: prev.UPIVPA || "user@upi",
            Is_KYC_Done: prev.Is_KYC_Done || "1",
          }));
        } else {
          console.error("Balance Enquiry Failed:", response.data.Message);
        }
      } catch (error) {
        console.error("API call error:", error);
      }
    };

    fetchBalanceEnquiry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateback = () => navigate("/dashboard");

  return (
    <>
      <Box sx={{ px: { md: 6 } }} bgcolor={isDark ? "#0d1828" : "#ffffff"}>
        <DashboardHeader />
      </Box>

      <Box sx={{ background: isDark ? "#0d1828" : "#f7f7f7" }}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          sx={{ px: { xs: 2, md: 7 } }}
        >
          <Typography fontWeight={600} mt={3} sx={{ fontSize: { xs: 10, md: 20 } }}>
            Load Money To Wallet
          </Typography>
          <Button size="small" onClick={navigateback}>
            Back
          </Button>
        </Box>

        <Box
          sx={{
            px: { md: 7, xs: 2 },
            background: isDark ? "#0d1828" : "#f7f7f7",
            minHeight: "100vh",
            mt: 2,
          }}
        >
          <Card
            sx={{
              background: isDark ? "#132237" : "#ffffff",
              borderRadius: "16px",
              p: 3,
              mb: 4,
              border: isDark
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* LEFT SECTION */}
              <Box
                sx={{
                  width: { xs: "0%", md: "48%" },
                  display: { xs: "none", md: "block" },
                }}
              >
                <Typography
                  sx={{ fontWeight: 400, fontSize: 20, color: isDark ? "#fff" : "#000" }}
                >
                  Wallet Balance : ₹{user?.Balance}
                </Typography>

                <Box mt={2}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: isDark ? "#9bb3c9" : "#475467",
                      mb: 1,
                    }}
                  >
                    Add money to Wallet
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "18px",
                      background: isDark
                        ? "linear-gradient(135deg,#132237,#1b314d)"
                        : "linear-gradient(135deg,#f8fafc,#ffffff)",
                      border: "2px solid #22c55e",
                      boxShadow: isDark
                        ? "0 0 20px rgba(34,197,94,0.25)"
                        : "0 4px 20px rgba(34,197,94,0.15)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 700, color: "#22c55e", mb: 1 }}
                    >
                      Enter Amount{" "}
                      <Typography
                        component="span"
                        sx={{ fontSize: 13, fontWeight: 600, color: "#f59e0b" }}
                      >
                        (₹{MIN_AMOUNT.toFixed(2)} – ₹{maxLabel})
                      </Typography>
                    </Typography>

                    <TextField
                      fullWidth
                      variant="standard"
                      value={amount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, "");
                        const parts = val.split(".");
                        if (parts.length > 2 || (parts[1] && parts[1].length > 2)) return;

                        const num = parseFloat(val);

                        if (val && !isNaN(num) && num > MAX_AMOUNT) {
                          setAmountError(`Maximum allowed is ₹${maxLabel}`);
                          return;
                        }

                        setAmount(val);

                        if (val && !isNaN(num) && num < MIN_AMOUNT) {
                          setAmountError(`Minimum amount should be ₹${MIN_AMOUNT}`);
                        } else {
                          setAmountError("");
                        }
                      }}
                      onBlur={() => {
                        const num = parseFloat(amount);
                        if (!amount || isNaN(num)) {
                          setAmount(MIN_AMOUNT.toFixed(2));
                          setAmountError("");
                          return;
                        }
                        if (num < MIN_AMOUNT) {
                          setAmountError(`Minimum amount should be ₹${MIN_AMOUNT}`);
                          return;
                        }
                        if (num > MAX_AMOUNT) {
                          setAmount(MAX_AMOUNT.toFixed(2));
                          setAmountError(`Maximum allowed is ₹${maxLabel}`);
                          return;
                        }
                        setAmount(num.toFixed(2));
                        setAmountError("");
                      }}
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                          <Typography
                            sx={{ fontSize: 34, fontWeight: 700, mr: 1, color: "#22c55e" }}
                          >
                            ₹
                          </Typography>
                        ),
                        inputMode: "decimal",
                        sx: {
                          fontSize: 36,
                          fontWeight: 700,
                          color: isDark ? "#fff" : "#111827",
                        },
                      }}
                      sx={{ "& input": { textAlign: "left" } }}
                      error={Boolean(amountError)}
                      helperText={amountError}
                    />

                    {/* Quick Amount Suggestions */}
                    <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                      {[500, 1000, 1500, 2000]
                        .filter((s) => s <= MAX_AMOUNT)
                        .map((suggestion) => (
                          <Button
                            key={suggestion}
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setAmount(suggestion.toFixed(2));
                              setAmountError("");
                            }}
                            sx={{
                              borderRadius: "20px",
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: 13,
                              px: 2,
                              py: 0.5,
                              borderColor: isDark ? "#2a5f3f" : "#22c55e",
                              color: isDark ? "#22c55e" : "#16a34a",
                              backgroundColor: isDark
                                ? "rgba(34,197,94,0.08)"
                                : "rgba(34,197,94,0.06)",
                              "&:hover": {
                                backgroundColor: isDark
                                  ? "rgba(34,197,94,0.18)"
                                  : "rgba(34,197,94,0.14)",
                                borderColor: "#22c55e",
                              },
                            }}
                          >
                            + ₹{suggestion}
                          </Button>
                        ))}
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: "12px",
                    background: isDark ? "#0f1a29" : "#fafafa",
                    border: isDark
                      ? "1px solid rgb(42,66,94)"
                      : "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 15,
                      mb: 1,
                      color: isDark ? "#9bb3c9" : "#333",
                    }}
                  >
                    Note:
                  </Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.6 }}>
                    1. Minimum Load Money amount is Rs.10/-. <br />
                    2. Do not use other wallets or virtual cards etc, for loading money.{" "}
                    <br />
                    3. If amount is debited and wallet is not credited contact customer
                    care or wait for 2 working days before complaining to the bank.
                  </Typography>
                </Box>

                <Typography
                  component={Link}
                  to="/helpcenterlayout"
                  sx={{
                    mt: 2,
                    color: "#12b76a",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Click here to view limits and charges
                </Typography>
              </Box>

              {/* RIGHT SECTION */}
              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <Typography
                  sx={{ fontWeight: 600, mb: 2, color: isDark ? "#fff" : "#000" }}
                >
                  Select Source
                </Typography>

                <Grid container spacing={2} justifyContent="left" display={"flex"}>
                  {[
                    {
                      title: "TAPay Credit/Debit Card",
                      subtitle: "Pay using your credit or debit card",
                      icon: <CreditCardIcon sx={{ fontSize: 26 }} />,
                      path: "/dashboard/loadmoney/usingccddc",
                      isTapay: true,
                    },
                    {
                      title: "TAPay Net Banking",
                      subtitle: "Login using bank credentials",
                      icon: <AccountBalanceIcon sx={{ fontSize: 26 }} />,
                      path: "/dashboard/loadmoney/usingnetbanking",
                      isTapay: true,
                    },
                    {
                      title: "PayU Credit/Debit Card",
                      subtitle: "Secure PayU card payments",
                      icon: <CreditScoreIcon sx={{ fontSize: 26 }} />,
                      path: "/dashboard/loadmoney/Payuccddc",
                      isTapay: false,
                    },
                    {
                      title: "PayU Net Banking",
                      subtitle: "Pay through PayU Net Banking",
                      icon: <AccountBalanceWalletIcon sx={{ fontSize: 26 }} />,
                      path: "/dashboard/loadmoney/Payunetbanking",
                      isTapay: false,
                    },
                    {
                      title: "NEFT",
                      subtitle: "Pay through NEFT",
                      icon: <AccountBalanceWalletIcon sx={{ fontSize: 26 }} />,
                      path: "/dashboard/loadmoney/usingneft",
                      isTapay: false,
                    },
                  ].map((method) => (
                    <Grid size={{ xs: 12, sm: 6 }} item xs={12} sm={6} key={method.title}>
                      <Box
                        onClick={() => {
                          if (method.isCashfree) {
                            if (cashfreeLoading) return;
                            handleCashfreePayment();
                            return;
                          }
                          if (method.title === "TAPay Net Banking") {
                            setComingSoonTitle(method.title);
                            setOpenComingSoon(true);
                            return;
                          }
                          if (method.isTapay) {
                            handleTapayPayment(method);
                          } else {
                            const num = parseFloat(amount);
                            if (!amount || isNaN(num) || num < MIN_AMOUNT) {
                              setAmountError(`Minimum amount should be ₹${MIN_AMOUNT}`);
                              return;
                            }
                            if (num > MAX_AMOUNT) {
                              setAmountError(`Maximum allowed is ₹${maxLabel}`);
                              return;
                            }
                            navigate(method.path, { state: { amount } });
                          }
                        }}
                        sx={{
                          mb: 1.5,
                          p: 2,
                          borderRadius: "12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          background: isDark ? "#0f1a29" : "#f8f8f8",
                          border: isDark
                            ? "1px solid #26405d"
                            : "1px solid rgba(0,0,0,0.15)",
                          transition: "0.3s ease",
                          "&:hover": { opacity: 0.85, transform: "translateY(-2px)" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: { xs: 30, sm: 36, md: 42 },
                              height: { xs: 30, sm: 36, md: 42 },
                              borderRadius: "10px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              background: "#c9e8d3ff",
                              border: "1px solid rgba(0,0,0,0.1)",
                              color: "green",
                            }}
                          >
                            {React.cloneElement(method.icon, {
                              sx: { fontSize: { xs: 16, sm: 20, md: 26 } },
                            })}
                          </Box>
                          <Box>
                            <Typography
                              sx={{ fontWeight: 600, fontSize: { md: 14, xs: 10 } }}
                            >
                              {method.title}
                            </Typography>
                            <Typography
                              sx={{ fontSize: { md: 12, xs: 8 }, color: "#888" }}
                            >
                              {method.subtitle}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton sx={{ color: isDark ? "white" : "black" }}>
                          <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Card>

          <LoadMoneyBankSection copyText={copyText} />
        </Box>
      </Box>

      {/* COMING SOON DIALOG */}
      <Dialog
        open={openComingSoon}
        onClose={() => setOpenComingSoon(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 3,
            width: 380,
            background: isDark
              ? "linear-gradient(180deg, #0f1a29, #132237)"
              : "linear-gradient(180deg, #ffffff, #f9fafb)",
          },
        }}
      >
        <IconButton
          onClick={() => setOpenComingSoon(false)}
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            width: 70,
            height: 70,
            borderRadius: "50%",
            background: "rgba(77,182,77,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <ConstructionIcon sx={{ fontSize: 36, color: "#4db64d" }} />
        </Box>
        <Typography textAlign="center" fontWeight={700} fontSize={18} mb={1}>
          Coming Soon 🚀
        </Typography>
        <Typography textAlign="center" fontSize={14} color="text.secondary" mb={3}>
          <b>{comingSoonTitle}</b> is currently under development.
          <br />
          We're working hard to bring this feature to you.
        </Typography>
        <Button
          fullWidth
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            color: "#ffffff",
            fontWeight: 600,
            bgcolor: "#4db64d",
            "&:hover": { bgcolor: "#43a047" },
          }}
          onClick={() => setOpenComingSoon(false)}
        >
          Ok
        </Button>
      </Dialog>

      {/* CASHFREE LOADING OVERLAY */}
      <Dialog
        open={cashfreeLoading}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: "20px", p: 3, textAlign: "center" } }}
      >
        <Box py={2}>
          <CircularProgress size={48} sx={{ color: "#1565c0", mb: 2 }} />
          <Typography fontWeight={700} fontSize={16} mb={0.5}>
            Processing Payment
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            Please do not close or refresh this page
          </Typography>
        </Box>
      </Dialog>

      {/* CASHFREE RESULT DIALOG */}
      <Dialog
        open={cashfreeResult?.open || false}
        onClose={() => setCashfreeResult(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            background: cashfreeResult?.success
              ? "linear-gradient(135deg, #16a34a, #22c55e)"
              : cashfreeResult?.pending
                ? "linear-gradient(135deg, #d97706, #f59e0b)"
                : "linear-gradient(135deg, #dc2626, #ef4444)",
            py: 3.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography sx={{ fontSize: 44 }}>
            {cashfreeResult?.success ? "🎉" : cashfreeResult?.pending ? "⏳" : "❌"}
          </Typography>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18, mt: 0.5 }}>
            {cashfreeResult?.success
              ? "Payment Successful!"
              : cashfreeResult?.pending
                ? "Payment Pending"
                : "Payment Failed"}
          </Typography>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, pb: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.7, mb: 2 }}>
            {cashfreeResult?.success
              ? `₹${amount} has been added to your wallet successfully.`
              : cashfreeResult?.pending
                ? "Your payment is being processed. Wallet will be credited automatically once confirmed."
                : cashfreeResult?.message}
          </Typography>

          {(cashfreeResult?.orderId || cashfreeResult?.balance || cashfreeResult?.txnId) && (
            <Box sx={{ background: "#f8faff", borderRadius: "12px", p: 2, textAlign: "left" }}>
              {cashfreeResult?.orderId && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography fontSize={12} color="text.secondary">Order ID</Typography>
                  <Typography fontSize={12} fontWeight={700} sx={{ fontFamily: "monospace" }}>
                    {cashfreeResult.orderId}
                  </Typography>
                </Box>
              )}
              {cashfreeResult?.txnId && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography fontSize={12} color="text.secondary">Transaction ID</Typography>
                  <Typography fontSize={12} fontWeight={700}>{cashfreeResult.txnId}</Typography>
                </Box>
              )}
              {cashfreeResult?.balance && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography fontSize={12} color="text.secondary">Wallet Balance</Typography>
                  <Typography fontSize={13} fontWeight={800} sx={{ color: "#16a34a" }}>
                    ₹{cashfreeResult.balance}
                  </Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between">
                <Typography fontSize={12} color="text.secondary">Amount</Typography>
                <Typography
                  fontSize={13}
                  fontWeight={800}
                  sx={{
                    color: cashfreeResult?.success
                      ? "#16a34a"
                      : cashfreeResult?.pending
                        ? "#d97706"
                        : "#dc2626",
                  }}
                >
                  ₹{parseFloat(amount).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ px: 3, pb: 3, pt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          {cashfreeResult?.success && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                setCashfreeResult(null);
                navigate("/dashboard");
              }}
              sx={{
                borderRadius: "14px",
                py: 1.4,
                fontWeight: 700,
                textTransform: "none",
                background: "linear-gradient(135deg, #16a34a, #22c55e)",
                boxShadow: "0 4px 14px rgba(22,163,74,0.35)",
              }}
            >
              Back to Dashboard
            </Button>
          )}
          {cashfreeResult?.pending && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                setCashfreeResult(null);
                navigate("/dashboard");
              }}
              sx={{
                borderRadius: "14px",
                py: 1.4,
                fontWeight: 700,
                textTransform: "none",
                background: "linear-gradient(135deg, #d97706, #f59e0b)",
              }}
            >
              Go to Dashboard
            </Button>
          )}
          {!cashfreeResult?.success && !cashfreeResult?.pending && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => setCashfreeResult(null)}
              sx={{
                borderRadius: "14px",
                py: 1.4,
                fontWeight: 700,
                textTransform: "none",
                background: "linear-gradient(135deg, #dc2626, #ef4444)",
              }}
            >
              Try Again
            </Button>
          )}
          <Button
            fullWidth
            onClick={() => setCashfreeResult(null)}
            sx={{ textTransform: "none", borderRadius: "14px", color: "text.secondary" }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </>
  );
}

function FieldRow({ label, value, copyText }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        mb: 2,
        background: isDark ? "#0f1a29" : "#e9f9ee",
        borderRadius: "10px",
        p: 2,
        px: 4,
        border: isDark ? "1px solid #1d334a" : "1px solid rgba(0,0,0,0.1)",
      }}
    >
      <Typography sx={{ fontSize: 13, opacity: 0.7 }}>{label}</Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{value}</Typography>
        <IconButton onClick={() => copyText(value)}>
          <ContentCopyIcon sx={{ fontSize: 18, color: isDark ? "#9bb3c9" : "#444" }} />
        </IconButton>
      </Box>
    </Box>
  );
}